import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { useQuery } from '@tanstack/react-query';
import { AsyncBoundary, Icon } from '@kollez/design-system';
import { apiGet } from '@/lib/api';
import { usePartnerPipeline, usePartnerCollegesByMou } from '@/lib/queries';
import { formatLPA } from '@kollez/shared';

/**
 * /group/partners/:id — direct port of kollez/pages/group/partner.html.
 *   5-KPI + tabs (Hiring / Drives / Agreements / Feedback / Contacts).
 */
type Tab = 'hires' | 'drives' | 'mous' | 'fb' | 'people';

interface PartnerDoc {
  _id: string;
  name: string;
  industry?: string;
  headquarters?: { city?: string; state?: string };
  partnerSince?: number;
  scope: string;
  status?: string;
  logoUrl?: string;
}
interface MoUDoc { _id: string; status: string; term?: { start?: string; end?: string } }

export function GroupPartnerDetailPage() {
  const { id } = useParams();
  const [tab, setTab] = useState<Tab>('hires');
  const query = useQuery({
    queryKey: ['group', 'partner', id],
    queryFn: () => apiGet<{
      partner: PartnerDoc;
      mous: MoUDoc[];
      colleges: Array<{ _id: string; name: string }>;
      stats?: { applications: number; drives: number; offersReleased: number; offersAccepted: number; medianCtcInr: number; acceptanceRatePct: number };
    }>(`/group/partners/${id}`),
    enabled: !!id,
  });
  const partner = query.data?.partner;
  const mous = query.data?.mous ?? [];
  const colleges = query.data?.colleges ?? [];
  const stats = query.data?.stats;
  const partnerId = partner?._id;
  const pipelineQ = usePartnerPipeline(partnerId);
  const mouCollegesQ = usePartnerCollegesByMou(partnerId);
  const pipeline = pipelineQ.data;
  const mouColleges = mouCollegesQ.data?.colleges ?? [];

  const activeMou = mous.find((m) => m.status === 'Active');
  const initials = (partner?.name ?? 'W').split(/\s+/).map((s) => s[0]).join('').slice(0, 2).toUpperCase();

  return (
    <div className="stack">
      <div className="page-header">
        <div className="flex items-center gap-4 min-w-0">
          <span className="logo-tile" style={{ width: 56, height: 56, fontSize: '1.125rem' }}>{initials}</span>
          <div className="ph-text">
            <p className="eyebrow">
              {partner?.industry?.replace(/_/g, ' ') ?? 'Industry'}
              {partner?.partnerSince ? ` · partner since ${partner.partnerSince}` : ''}
              {partner?.headquarters?.city ? ` · ${partner.headquarters.city}` : ''}
            </p>
            <h1 className="h-page">{partner?.name ?? 'Partner'}</h1>
            <p className="subhead">
              {mous.length > 0
                ? `${mous.length} MoU${mous.length === 1 ? '' : 's'} with your group · ${colleges.length} college${colleges.length === 1 ? '' : 's'} in scope.`
                : 'No agreements with your group yet.'}
            </p>
          </div>
        </div>
        <div className="ph-actions">
          <Link className="btn btn-primary" to={`/group/mous`}><Icon name="file-signature" size={14} />New MoU</Link>
        </div>
      </div>

      <AsyncBoundary kind="detail" loading={query.isLoading} error={query.error}>
      <div className="grid gap-3 grid-cols-2 lg:grid-cols-5">
        {/* F-098: KPIs are live now. `stats` comes back from /group/partners/:id. */}
        <div className="card kpi"><span className="kpi-label">MoUs</span><span className="kpi-value">{mous.length}</span>{activeMou && <span className="kpi-meta">1 active</span>}</div>
        <div className="card kpi"><span className="kpi-label">Applications</span><span className="kpi-value">{stats?.applications ?? 0}</span><span className="kpi-meta">{stats?.drives ?? 0} drives</span></div>
        <div className="card kpi"><span className="kpi-label">Median CTC</span><span className="kpi-value">{stats?.medianCtcInr ? `₹${(stats.medianCtcInr / 100_000).toFixed(1)} LPA` : '—'}</span></div>
        <div className="card kpi"><span className="kpi-label">Offer acceptance</span><span className="kpi-value">{stats?.acceptanceRatePct != null ? `${stats.acceptanceRatePct}%` : '—'}</span><span className="kpi-meta">{stats?.offersAccepted ?? 0} of {stats?.offersReleased ?? 0}</span></div>
        <div className="card kpi"><span className="kpi-label">Colleges scoped</span><span className="kpi-value">{colleges.length}</span><span className="kpi-meta">via MoUs</span></div>
      </div>

      <div className="tabs" role="tablist">
        {([['hires', 'Hiring'], ['drives', 'Drives'], ['mous', 'Agreements'], ['fb', 'Feedback'], ['people', 'Contacts']] as const).map(([k, l]) => (
          <button key={k} className="tab" role="tab" aria-selected={tab === k} onClick={() => setTab(k)}>{l}</button>
        ))}
      </div>

      {tab === 'hires' && (
        <section className="grid gap-5 lg:grid-cols-2">
          <div className="card card-pad grid gap-3">
            <h2 className="h-section">Applications by status</h2>
            {pipelineQ.isLoading ? (
              <p className="cell-sub m-0">Loading…</p>
            ) : pipeline && Object.keys(pipeline.applicationsByStatus).length > 0 ? (
              <StatusBarList byStatus={pipeline.applicationsByStatus} />
            ) : (
              <p className="cell-sub m-0">
                No applications through this partner yet. The bar chart populates once students
                start applying to their drives.
              </p>
            )}
          </div>

          <div className="card">
            <div className="card-head">
              <h2 className="h-section">MoU-scoped colleges</h2>
              <span className="helper">{mouColleges.length} active/signed</span>
            </div>
            {mouCollegesQ.isLoading ? (
              <p className="cell-sub p-4 m-0">Loading…</p>
            ) : mouColleges.length === 0 ? (
              <p className="cell-sub p-4 m-0">
                No colleges scoped via an active MoU. Sign one from <b>Group → MoUs</b>.
              </p>
            ) : (
              <div className="tbl-wrap">
                <table className="tbl">
                  <thead><tr><th>College</th><th>Status</th><th className="r hide-mobile">Term ends</th></tr></thead>
                  <tbody>
                    {mouColleges.map((m) => {
                      const tenant = typeof m.tenant === 'object' ? m.tenant : null;
                      return (
                        <tr key={m._id}>
                          <td>
                            <div className="cell-title">{tenant?.displayName ?? tenant?.slug ?? '—'}</div>
                            {m.kind && <div className="cell-sub">{m.kind}</div>}
                          </td>
                          <td><span className={`badge ${m.status === 'Active' ? 'badge-success' : ''}`}>{m.status}</span></td>
                          <td className="r hide-mobile cell-sub">
                            {m.term?.endsAt
                              ? new Date(m.term.endsAt).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' })
                              : '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>
      )}

      {tab === 'drives' && (
        <section className="card card-pad grid gap-3">
          {pipelineQ.isLoading ? (
            <p className="cell-sub m-0">Loading…</p>
          ) : pipeline && Object.keys(pipeline.opportunities).length > 0 ? (
            <>
              <div className="flex items-baseline justify-between">
                <h2 className="h-section m-0">Opportunities by status</h2>
                {pipeline.medianCTCInr && (
                  <span className="cell-sub">Median accepted CTC: {formatLPA(pipeline.medianCTCInr)}</span>
                )}
              </div>
              <StatusBarList byStatus={pipeline.opportunities} />

              <h2 className="h-section mt-4">Offers by status</h2>
              {Object.keys(pipeline.offersByStatus).length > 0 ? (
                <StatusBarList byStatus={pipeline.offersByStatus} />
              ) : (
                <p className="cell-sub m-0">No offers uploaded through this partner yet.</p>
              )}
            </>
          ) : (
            <p className="cell-sub m-0">
              This partner hasn't posted any opportunities. Once they do, this tab shows their
              per-status funnel across every drive.
            </p>
          )}
        </section>
      )}

      {tab === 'mous' && (
        <section className="card">
          <p className="helper p-4 m-0">
            Live MoU list for this partner ships in a follow-up. Open <b>Group → MoUs</b> and filter by partner.
          </p>
        </section>
      )}

      {tab === 'fb' && (
        <section className="card">
          <p className="helper p-4 m-0">
            Student feedback ratings + recruiter debrief quotes need feedback-collection endpoints
            (drive-feedback + partner-note) — planned. Nothing hardcoded here.
          </p>
        </section>
      )}

      {tab === 'people' && (
        <section className="card">
          <p className="helper p-4 m-0">
            Wires to <code className="code-inline">GET /partners/:id/users</code> so recruiter roster
            appears here, once we surface Partner.contacts[]. Deferred.
          </p>
        </section>
      )}
      </AsyncBoundary>
    </div>
  );
}

/**
 * Compact bar list for a {status: count} record. Bars scale to the local max
 * so a single dominant status doesn't flatten the others. Used for both the
 * application-status and opportunity-status views sourced from
 * /partners/:id/pipeline.
 */
function StatusBarList({ byStatus }: { byStatus: Record<string, number> }) {
  const rows = Object.entries(byStatus).sort((a, b) => b[1] - a[1]);
  const max = Math.max(1, ...rows.map(([, n]) => n));
  return (
    <ol className="stack">
      {rows.map(([status, n]) => (
        <li key={status} className="grid grid-cols-[minmax(0,1fr)_60px_44px] items-center gap-2">
          <span className="truncate"><span className="badge">{status.replace(/_/g, ' ')}</span></span>
          <span className="bar"><span style={{ width: `${Math.max(4, Math.round((n / max) * 100))}%` }} /></span>
          <span className="text-right text-sm">{n.toLocaleString('en-IN')}</span>
        </li>
      ))}
    </ol>
  );
}
